/**
 * Global Blacklisted Persons — shared service.
 *
 * The blacklist is global (one table, read by every agency, written by the SA).
 * A record is a flat object stored in blacklist.data. This module centralises:
 *   - the field schema (used by the form, import template and export)
 *   - full-name generation
 *   - client matching for automatic contract verification
 *   - audit logging (with best-effort IP / device / browser)
 *   - import (xlsx/xls/csv) parsing + export (xlsx/csv) helpers
 */

import * as XLSX from 'xlsx';
import { supabase } from '../../../config/supabase';

// ── Field schema ────────────────────────────────────────────────────────────
export const RISK_LEVELS = ['Low', 'Medium', 'High', 'Critical'];
export const STATUSES = ['Active', 'Inactive'];

// key → { label, group, import header aliases }
export const FIELDS = [
  { key: 'firstName', label: 'Prénom', group: 'Personnel', aliases: ['prenom', 'prénom', 'first name', 'firstname'] },
  { key: 'lastName', label: 'Nom', group: 'Personnel', aliases: ['nom', 'last name', 'lastname'] },
  { key: 'gender', label: 'Genre', group: 'Personnel', aliases: ['genre', 'gender', 'sexe'] },
  { key: 'birthDate', label: 'Date de naissance', group: 'Personnel', type: 'date', aliases: ['date de naissance', 'birth date', 'dob', 'naissance'] },
  { key: 'nationality', label: 'Nationalité', group: 'Personnel', aliases: ['nationalite', 'nationalité', 'nationality'] },
  { key: 'cnie', label: 'C.N.I.E', group: 'Identification', aliases: ['cnie', 'cin', 'c.n.i.e', 'national id', 'cni'] },
  { key: 'passport', label: 'Passeport', group: 'Identification', aliases: ['passport', 'passeport', 'passport number'] },
  { key: 'license', label: 'Permis de conduire', group: 'Identification', aliases: ['permis', 'license', 'driver license', 'permis de conduire'] },
  { key: 'phone', label: 'Téléphone', group: 'Contact', aliases: ['phone', 'telephone', 'téléphone', 'tel', 'gsm'] },
  { key: 'phone2', label: 'Téléphone secondaire', group: 'Contact', aliases: ['phone 2', 'telephone 2', 'secondary phone', 'tel2'] },
  { key: 'email', label: 'Email', group: 'Contact', aliases: ['email', 'e-mail', 'mail'] },
  { key: 'city', label: 'Ville', group: 'Adresse', aliases: ['ville', 'city'] },
  { key: 'address', label: 'Adresse', group: 'Adresse', aliases: ['adresse', 'address', 'full address'] },
  { key: 'postalCode', label: 'Code postal', group: 'Adresse', aliases: ['code postal', 'postal code', 'zip'] },
  { key: 'country', label: 'Pays', group: 'Adresse', aliases: ['pays', 'country'] },
  { key: 'reason', label: 'Motif', group: 'Location', aliases: ['motif', 'reason', 'blacklist reason'] },
  { key: 'incident', label: 'Description de l\'incident', group: 'Location', aliases: ['incident', 'description', 'incident description'] },
  { key: 'outstanding', label: 'Montant dû', group: 'Location', aliases: ['montant', 'outstanding', 'amount', 'montant du', 'outstanding amount'] },
  { key: 'currency', label: 'Devise', group: 'Location', aliases: ['devise', 'currency'] },
  { key: 'riskLevel', label: 'Niveau de risque', group: 'Location', aliases: ['risque', 'risk', 'risk level', 'niveau de risque'] },
  { key: 'status', label: 'Statut', group: 'Location', aliases: ['statut', 'status'] },
  { key: 'sourceAgency', label: 'Agence source', group: 'Admin', aliases: ['agence', 'agency', 'source agency', 'agence source'] },
  { key: 'approvedBy', label: 'Approuvé par', group: 'Admin', aliases: ['approuve par', 'approved by'] },
  { key: 'notes', label: 'Notes', group: 'Admin', aliases: ['notes', 'note', 'remarque'] },
];

export const emptyRecord = () => ({
  firstName: '', lastName: '', gender: '', birthDate: '', nationality: '',
  cnie: '', passport: '', license: '',
  phone: '', phone2: '', email: '',
  city: '', address: '', postalCode: '', country: 'Maroc',
  reason: '', incident: '', outstanding: '', currency: 'DH',
  riskLevel: 'Medium', status: 'Active',
  sourceAgency: '', approvedBy: '', notes: '',
  attachments: [],
});

export const fullName = (r) => `${(r.firstName || '').trim()} ${(r.lastName || '').trim()}`.trim();

// ── Agency submission (request) schema ──────────────────────────────────────
export const REQUEST_FIELDS = [
  { key: 'firstName', label: 'Prénom', group: 'Personnel', required: true },
  { key: 'lastName', label: 'Nom', group: 'Personnel', required: true },
  { key: 'birthDate', label: 'Date de naissance', group: 'Personnel', type: 'date' },
  { key: 'nationality', label: 'Nationalité', group: 'Personnel' },
  { key: 'cnie', label: 'C.N.I.E', group: 'Identification' },
  { key: 'license', label: 'Permis de conduire', group: 'Identification' },
  { key: 'passport', label: 'Passeport (optionnel)', group: 'Identification' },
  { key: 'phone', label: 'Téléphone', group: 'Contact' },
  { key: 'email', label: 'Email (optionnel)', group: 'Contact' },
  { key: 'city', label: 'Ville', group: 'Adresse' },
  { key: 'address', label: 'Adresse', group: 'Adresse' },
  { key: 'vehicleReg', label: 'Immatriculation véhicule', group: 'Incident' },
  { key: 'contractNumber', label: 'N° contrat de location', group: 'Incident' },
  { key: 'incidentDate', label: 'Date de l\'incident', group: 'Incident', type: 'date' },
  { key: 'reason', label: 'Motif du signalement', group: 'Incident' },
  { key: 'incident', label: 'Description détaillée', group: 'Incident' },
  { key: 'damageAmount', label: 'Montant des dégâts', group: 'Incident' },
  { key: 'outstanding', label: 'Montant impayé', group: 'Incident' },
  { key: 'currency', label: 'Devise', group: 'Incident' },
  { key: 'riskLevel', label: 'Niveau de risque', group: 'Incident' },
  { key: 'notes', label: 'Notes de l\'agence', group: 'Incident' },
];

export const emptyRequest = () => ({
  firstName: '', lastName: '', birthDate: '', nationality: '',
  cnie: '', license: '', passport: '',
  phone: '', email: '', city: '', address: '',
  vehicleReg: '', contractNumber: '', incidentDate: '',
  reason: '', incident: '', damageAmount: '', outstanding: '', currency: 'DH',
  riskLevel: 'Medium', notes: '',
  attachments: [],
  status: 'pending',
});

// Convert an approved request into a global blacklist record.
export const requestToRecord = (req, approver) => ({
  firstName: req.firstName, lastName: req.lastName, gender: req.gender || '',
  birthDate: req.birthDate, nationality: req.nationality,
  cnie: req.cnie, passport: req.passport, license: req.license,
  phone: req.phone, phone2: '', email: req.email,
  city: req.city, address: req.address, postalCode: '', country: req.country || 'Maroc',
  reason: req.reason, incident: req.incident,
  outstanding: req.outstanding || req.damageAmount || '', currency: req.currency || 'DH',
  riskLevel: req.riskLevel || 'Medium', status: 'Active',
  sourceAgency: req.agencyName || req.agency_id || '',
  approvedBy: approver || 'super-admin',
  notes: [req.notes, req.vehicleReg ? `Véhicule: ${req.vehicleReg}` : '', req.contractNumber ? `Contrat: ${req.contractNumber}` : ''].filter(Boolean).join(' · '),
  attachments: Array.isArray(req.attachments) ? req.attachments : [],
  fullName: fullName(req),
  addedAt: new Date().toISOString(),
});

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
const digits = (v) => String(v == null ? '' : v).replace(/\D/g, '');

// ── Matching for automatic contract verification ────────────────────────────
// Returns the list of matched blacklist records for a rental client, with the
// fields that matched. Only ACTIVE records are considered.
export const matchClient = (client, list = []) => {
  const c = {
    cnie: norm(client.clientCin), passport: norm(client.clientPassport), license: norm(client.clientPermis),
    phone: digits(client.clientPhone), email: norm(client.clientEmail),
    name: norm(client.clientName), birth: norm(client.clientBirthDate), city: norm(client.clientCity || client.clientVille),
  };
  const matches = [];
  for (const rec of list) {
    if (norm(rec.status) === 'inactive') continue;
    const reasons = [];
    if (c.cnie && rec.cnie && norm(rec.cnie) === c.cnie) reasons.push('C.N.I.E');
    if (c.passport && rec.passport && norm(rec.passport) === c.passport) reasons.push('Passeport');
    if (c.license && rec.license && norm(rec.license) === c.license) reasons.push('Permis');
    if (c.phone && c.phone.length >= 6 && (digits(rec.phone) === c.phone || digits(rec.phone2) === c.phone)) reasons.push('Téléphone');
    if (c.email && rec.email && norm(rec.email) === c.email) reasons.push('Email');
    const recFull = norm(fullName(rec));
    if (c.name && recFull && recFull === c.name) reasons.push('Nom complet');
    if (c.birth && rec.birthDate && norm(rec.birthDate) === c.birth && c.name && recFull === c.name) reasons.push('Date de naissance');
    if (reasons.length) matches.push({ record: rec, reasons });
  }
  // Highest risk first.
  const order = { critical: 0, high: 1, medium: 2, low: 3 };
  matches.sort((a, b) => (order[norm(a.record.riskLevel)] ?? 9) - (order[norm(b.record.riskLevel)] ?? 9));
  return matches;
};

// ── Audit log ───────────────────────────────────────────────────────────────
let _cachedIp = null;
async function getIp() {
  if (_cachedIp !== null) return _cachedIp;
  try {
    const r = await fetch('https://api.ipify.org?format=json');
    const j = await r.json();
    _cachedIp = j.ip || '';
  } catch (_) { _cachedIp = ''; }
  return _cachedIp;
}
function deviceInfo() {
  const ua = navigator.userAgent || '';
  let browser = 'Inconnu';
  if (/edg/i.test(ua)) browser = 'Edge';
  else if (/chrome|crios/i.test(ua)) browser = 'Chrome';
  else if (/firefox|fxios/i.test(ua)) browser = 'Firefox';
  else if (/safari/i.test(ua)) browser = 'Safari';
  const device = /mobile/i.test(ua) ? 'Mobile' : /tablet|ipad/i.test(ua) ? 'Tablette' : 'Ordinateur';
  return { browser, device, ua };
}

export const logAudit = async (action, { actor = '', agencyId = '', targetId = '', targetName = '', details = {} } = {}) => {
  try {
    const info = deviceInfo();
    const ip = await getIp();
    await supabase.from('blacklist_audit').insert({
      action, actor, agency_id: agencyId, target_id: targetId, target_name: targetName,
      ip, user_agent: info.ua,
      details: { ...details, browser: info.browser, device: info.device },
    });
  } catch (_) { /* audit must never block the main action */ }
};

// ── Duplicate detection ─────────────────────────────────────────────────────
// Two records are the "same person" if they share a strong identifier.
export const isSamePerson = (a, b) =>
  (a.cnie && norm(a.cnie) === norm(b.cnie)) ||
  (a.passport && norm(a.passport) === norm(b.passport)) ||
  (a.license && norm(a.license) === norm(b.license)) ||
  (a.phone && digits(a.phone) && digits(a.phone) === digits(b.phone)) ||
  (fullName(a) && norm(fullName(a)) === norm(fullName(b)) && a.birthDate && norm(a.birthDate) === norm(b.birthDate));

// ── Import (xlsx / xls / csv) ───────────────────────────────────────────────
export const parseImportFile = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  const headerMap = {};
  if (rows.length) {
    Object.keys(rows[0]).forEach(h => {
      const hn = norm(h);
      const f = FIELDS.find(f => f.key === hn || norm(f.label) === hn || (f.aliases || []).includes(hn));
      if (f) headerMap[h] = f.key;
    });
  }
  const records = rows.map(row => {
    const rec = emptyRecord();
    Object.entries(row).forEach(([h, v]) => { const k = headerMap[h]; if (k) rec[k] = String(v).trim(); });
    if (!rec.currency) rec.currency = 'DH';
    if (!rec.riskLevel) rec.riskLevel = 'Medium';
    if (!rec.status) rec.status = 'Active';
    return rec;
  }).filter(r => r.firstName || r.lastName || r.cnie || r.passport || r.phone);
  return records;
};

export const validateRecord = (r) => {
  const errs = [];
  if (!r.firstName && !r.lastName) errs.push('Nom/Prénom manquant');
  if (!r.cnie && !r.passport && !r.license && !r.phone) errs.push('Aucun identifiant (CNIE/Passeport/Permis/Tél)');
  if (r.riskLevel && !RISK_LEVELS.map(norm).includes(norm(r.riskLevel))) errs.push('Niveau de risque invalide');
  return errs;
};

// ── Export ──────────────────────────────────────────────────────────────────
const toAoa = (records) => {
  const cols = FIELDS.map(f => f.key);
  const head = FIELDS.map(f => f.label);
  const rows = records.map(r => cols.map(k => r[k] ?? ''));
  return [['Nom complet', ...head], ...records.map((r, i) => [fullName(r), ...rows[i]])];
};

export const exportExcel = (records, name = 'blacklist') => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toAoa(records)), 'Blacklist');
  XLSX.writeFile(wb, `${name}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

export const exportCsv = (records, name = 'blacklist') => {
  const aoa = toAoa(records);
  const csv = aoa.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a'); a.href = url; a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

export const downloadTemplate = () => {
  const head = FIELDS.map(f => f.label);
  const example = FIELDS.map(f => {
    if (f.key === 'firstName') return 'Ahmed'; if (f.key === 'lastName') return 'Bennani';
    if (f.key === 'cnie') return 'AB123456'; if (f.key === 'riskLevel') return 'High';
    if (f.key === 'status') return 'Active'; if (f.key === 'currency') return 'DH';
    return '';
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, example]), 'Template');
  XLSX.writeFile(wb, 'blacklist_import_template.xlsx');
};
